import { useEffect, useRef, useState } from "react";
import { ShareExpiry } from "@/components/ShareExpiry";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { daDate, holidayInfo, holidayLabel, holidaySegments, barPieces } from "@/lib/date";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UserSingleSelect } from "@/components/UserSingleSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DanishDatePicker } from "@/components/DanishDatePicker";
import { ResponsibleMultiSelect } from "@/components/ResponsibleMultiSelect";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Paperclip, Upload, Download, Trash2, FileText, GanttChartSquare, ChevronUp, ChevronDown, Plus, Share2, Copy, ExternalLink, Users, CalendarClock, Building2, User as UserIcon, Clock, Wallet, MessageSquare, Info, ClipboardList, NotebookText, Pencil, Eye, X, Check } from "lucide-react";
import { MultiSelect } from "@/components/MultiSelect";

const DAY = 86400000, DAY_W = 26, NONE = "__none__";
const roleLabel = { admin: "Administrator", projektleder: "Projektleder", medarbejder: "Medarbejder" };
const STATUSES = ["Ikke startet", "I gang", "Afventer", "Færdig"];
const statusStyle = {
  "Ikke startet": "bg-stone-50 text-stone-500 border-stone-100",
  "I gang": "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]",
  "Afventer": "bg-[#F9F1D9] text-[#8A6E17] border-[#ECE0B6]",
  "Færdig": "bg-[#E7EADF] text-[#7E856C] border-[#D3D8C6]",
};
const STEP_STYLE = {
  "Ikke startet": { border: "border-[#EBE8E3]", dot: "bg-white border-[#D6D3D1]" },
  "I gang": { border: "border-[#E6DDD6]/70", dot: "bg-[#A08979] border-[#E6DDD6]" },
  "Afventer": { border: "border-[#C9A83F]/70", dot: "bg-[#C9A83F] border-[#C9A83F]" },
  "Færdig": { border: "border-[#7E856C]/70", dot: "bg-[#7E856C] border-[#7E856C]" },
};
const statusMeta = {
  ikke_igangsat: { label: "Ikke igangsat", dot: "bg-[#A8A29E]" },
  aktiv: { label: "Aktiv", dot: "bg-[#A83232]" },
  paused: { label: "Pauset", dot: "bg-[#A08979]" },
  afsluttet: { label: "Afsluttet", dot: "bg-[#7E856C]" },
};
const today = () => new Date().toISOString().slice(0, 10);
const fmtSize = (n) => !n ? "" : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const initials = (name) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");

function RespChip({ user }) {
  const url = user?.avatar_url ? `${BACKEND}${user.avatar_url}` : null;
  return (
    <span className="flex items-center gap-1.5 bg-[#F3F1EC] border border-[#EBE8E3] rounded-full pl-0.5 pr-2.5 py-0.5" data-testid={`resp-chip-${user.id}`}>
      {url ? (
        <img src={url} alt={user.name} className="h-6 w-6 rounded-full object-cover" style={{ border: "1px solid #EBE8E3" }} />
      ) : (
        <span className="h-6 w-6 rounded-full bg-[#F1DEDA] text-[#A83232] text-[10px] font-semibold flex items-center justify-center" style={{ border: "1px solid #E9C9C3" }}>{initials(user.name) || "?"}</span>
      )}
      <span className="text-xs font-medium text-[#1A1919]">{user.name}</span>
    </span>
  );
}

function CircularProgress({ value }) {
  const r = 34, c = 2 * Math.PI * r, off = c - (value / 100) * c;
  return (
    <div className="relative h-24 w-24 shrink-0">
      <svg className="h-24 w-24 -rotate-90" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#ECEAE5" strokeWidth="7" />
        <circle cx="40" cy="40" r={r} fill="none" stroke="#A83232" strokeWidth="7" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} className="transition-all duration-700 ease-out" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xl font-semibold text-[#1A1919]" data-testid="progress-pct">{value}%</span>
      </div>
    </div>
  );
}

function FileArea({ projectId, stepId, files, onChange, canEdit }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const scoped = files.filter((f) => (stepId == null ? f.step_id == null : f.step_id === stepId));
  const upload = async (file) => {
    if (!file) return; setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("project_id", projectId);
    if (stepId != null) fd.append("step_id", stepId);
    try { await api.post("/uploads", fd); toast.success("Fil uploadet", { description: file.name }); onChange(); }
    catch (e) { toast.error("Upload fejlede"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const download = async (f) => {
    const res = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a");
    a.href = url; a.download = f.original_filename; a.click(); URL.revokeObjectURL(url);
  };
  const del = async (f) => { await api.delete(`/files/${f.id}`); toast.success("Fil slettet"); onChange(); };
  const toggleVisible = async (f, c) => {
    try { await api.patch(`/files/${f.id}/visibility`, { visible_to_customer: c }); toast.success(c ? "Filen deles nu med kunden" : "Filen er skjult for kunden"); onChange(); }
    catch (e) { toast.error("Kunne ikke ændre synlighed"); }
  };
  return (
    <div className="space-y-2">
      <input ref={inputRef} type="file" hidden onChange={(e) => upload(e.target.files[0])} data-testid={stepId == null ? "project-file-input" : `step-file-input-${stepId}`} />
      {scoped.map((f) => (
        <div key={f.id} className="flex items-center gap-2 bg-[#F3F1EC] border border-[#EBE8E3] rounded-xl px-3 py-2.5" data-testid={`file-item-${f.id}`}>
          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{f.original_filename}</p><p className="text-xs text-muted-foreground">{fmtSize(f.size)} · {f.uploaded_by_name}</p></div>
          {canEdit && (
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0 mr-1" title="Gør filen synlig på kundelinket">
              <Switch checked={!!f.visible_to_customer} onCheckedChange={(c) => toggleVisible(f, c)} data-testid={`file-visible-${f.id}`} />
              <span className="hidden sm:inline">Vis for kunde</span>
            </label>
          )}
          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-xl" onClick={() => download(f)} data-testid={`download-file-${f.id}`}><Download className="h-4 w-4" /></Button>
          {canEdit && <Button size="icon" variant="ghost" className="h-8 w-8 rounded-xl text-destructive" onClick={() => del(f)} data-testid={`delete-file-${f.id}`}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      ))}
      {canEdit && <Button size="sm" variant="outline" className="rounded-xl" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={stepId == null ? "upload-project-file" : `upload-step-file-${stepId}`}><Upload className="h-3.5 w-3.5 mr-1.5" /> {busy ? "Uploader…" : "Vedhæft fil"}</Button>}
    </div>
  );
}

const HOLIDAY_HATCH = "repeating-linear-gradient(45deg, rgba(201,168,63,0.18), rgba(201,168,63,0.18) 6px, transparent 6px, transparent 12px)";

function Gantt({ steps }) {
  const rows = steps.filter((s) => s.selected && s.start_date && s.expected_end_date);
  if (!rows.length) return <p className="text-muted-foreground text-sm p-6">Ingen datoer. Angiv startdato på projektet for at se tidslinjen.</p>;
  const min = Math.min(...rows.map((r) => new Date(r.start_date).getTime()));
  const max = Math.max(...rows.map((r) => new Date(r.expected_end_date).getTime()));
  const totalDays = Math.max(1, Math.round((max - min) / DAY) + 1);
  const width = totalDays * DAY_W, t0 = today();
  const shades = holidaySegments(min, max);
  const ticks = [];
  for (let d = 0; d <= totalDays; d += 7) ticks.push({ d, label: new Date(min + d * DAY).toLocaleDateString("da-DK", { day: "2-digit", month: "short" }) });
  return (
    <div className="overflow-x-auto border border-[#EBE8E3] rounded-2xl bg-white">
      <div className="min-w-max">
        <div className="flex border-b border-[#EBE8E3] bg-[#FBFAF8]">
          <div className="w-56 shrink-0 px-4 py-2 label-caps border-r border-border">Trin</div>
          <div className="relative" style={{ width }}>{shades.map((sh, i) => <div key={`hh${i}`} className="absolute top-0 bottom-0 pointer-events-none" style={{ left: sh.offset * DAY_W, width: sh.len * DAY_W, background: HOLIDAY_HATCH }} />)}{ticks.map((tk) => <div key={tk.d} className="absolute top-0 h-full text-xs text-muted-foreground border-l border-border/60 pl-1 py-2" style={{ left: tk.d * DAY_W }}>{tk.label}</div>)}<div className="py-2 opacity-0">.</div></div>
        </div>
        {rows.map((s) => {
          const dur = Math.max(1, Math.round((new Date(s.expected_end_date).getTime() - new Date(s.start_date).getTime()) / DAY));
          const pieces = barPieces(new Date(s.start_date).getTime(), new Date(s.expected_end_date).getTime(), min);
          const widest = pieces.reduce((a, b, ix) => (b.len > pieces[a].len ? ix : a), 0);
          const delayed = s.status !== "Færdig" && s.expected_end_date < t0;
          const color = s.status === "Færdig" ? "bg-[#7E856C]" : delayed ? "bg-primary" : "bg-[#6c6c6c]";
          return (
            <div key={s.id} className="flex items-center border-b border-border last:border-0">
              <div className="w-56 shrink-0 px-4 py-2 text-sm border-r border-border truncate">{s.name}</div>
              <div className="relative h-11" style={{ width }}>
                {shades.map((sh, i) => <div key={`hr${i}`} className="absolute top-0 bottom-0 pointer-events-none" style={{ left: sh.offset * DAY_W, width: sh.len * DAY_W, background: HOLIDAY_HATCH }} />)}
                {pieces.map((p, pi) => (
                  <div key={pi} className={`absolute top-1/2 -translate-y-1/2 h-6 rounded-xl ${color} flex items-center px-2 text-[11px] text-white font-medium overflow-hidden`} style={{ left: p.offset * DAY_W, width: p.len * DAY_W }} title={`${daDate(s.start_date)} → ${daDate(s.expected_end_date)}${pieces.length > 1 ? " (pauser i ferien)" : ""}`}>{pi === widest ? `${dur}d` : ""}</div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const HOURLY_RATE = 520;
const fmtKr = (n) => new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 }).format(Math.round(n || 0)) + " kr";
const fmtH = (n) => new Intl.NumberFormat("da-DK", { maximumFractionDigits: 2 }).format(Number(n) || 0) + " t";
const daNum = (n) => (n == null || n === "" ? "" : String(n).replace(".", ","));

function budgetColor(hasBudget, pct) {
  if (!hasBudget) return { bar: "#A8A29E", text: "#78716C", badgeBg: "#F5F5F4", badgeTx: "#57534E" };
  if (pct > 100) return { bar: "#A83232", text: "#A83232", badgeBg: "#F1DEDA", badgeTx: "#A83232" };
  if (pct >= 80) return { bar: "#A08979", text: "#806B61", badgeBg: "#F4EFEC", badgeTx: "#806B61" };
  return { bar: "#7E856C", text: "#7E856C", badgeBg: "#E7EADF", badgeTx: "#7E856C" };
}

function BudgetBar({ pct, color }) {
  return (
    <div className="h-2 rounded-full overflow-hidden bg-[#ECEAE5]">
      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />
    </div>
  );
}

function EcoMetric({ label, value, testid, strong }) {
  return (
    <div data-testid={testid}>
      <p className="label-caps mb-1">{label}</p>
      <p className={strong ? "text-[#1A1919] font-semibold" : "text-[#1A1919]"}>{value}</p>
    </div>
  );
}

function EconomyPanel({ steps, canEdit, onSaveBudget, lines, onSaveLines }) {
  const [clines, setClines] = useState(lines || []);
  useEffect(() => { setClines(lines || []); }, [lines]);
  const rows = steps.map((s) => {
    const bud = Number(s.budgeted_hours) || 0;
    const real = Number(s.hours_spent) || 0;
    const hasBudget = bud > 0;
    const pct = hasBudget ? (real / bud * 100) : 0;
    return { s, bud, real, hasBudget, pct, budKr: bud * HOURLY_RATE, realKr: real * HOURLY_RATE };
  });
  const totBudH = rows.reduce((a, r) => a + r.bud, 0);
  const totRealH = rows.reduce((a, r) => a + r.real, 0);
  const custBudKr = clines.reduce((a, l) => a + (Number(l.budget_kr) || 0), 0);
  const custRealKr = clines.reduce((a, l) => a + (Number(l.realized_kr) || 0), 0);
  const totBudKr = totBudH * HOURLY_RATE + custBudKr;
  const totRealKr = totRealH * HOURLY_RATE + custRealKr;
  const anyBudget = totBudKr > 0;
  const totPct = anyBudget ? (totRealKr / totBudKr * 100) : 0;
  const totCol = budgetColor(anyBudget, totPct);

  const saveLines = (arr) => onSaveLines((arr || clines).map((l) => ({ id: l.id, label: l.label, budget_kr: l.budget_kr, realized_kr: l.realized_kr })));
  const addLine = () => { const next = [...clines, { id: `tmp-${Date.now()}`, label: "", budget_kr: null, realized_kr: null }]; setClines(next); saveLines(next); };
  const removeLine = (i) => { const next = clines.filter((_, j) => j !== i); setClines(next); saveLines(next); };
  const commit = (i, patch) => { const next = clines.map((l, j) => j === i ? { ...l, ...patch } : l); setClines(next); saveLines(next); };
  const parseKr = (str) => { const raw = (str || "").replace(/\s/g, "").replace(/\./g, "").replace(",", "."); if (raw === "") return null; const v = parseFloat(raw); return isNaN(v) || v < 0 ? null : v; };

  return (
    <div className="space-y-6" data-testid="economy-panel">
      {/* Samlet projektøkonomi */}
      <div className="pcard p-6" data-testid="economy-summary">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <h3 className="font-semibold tracking-tight text-[#1A1919] flex items-center gap-2"><Wallet className="h-4 w-4 text-muted-foreground" /> Samlet projektøkonomi</h3>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: totCol.badgeBg, color: totCol.badgeTx }} data-testid="economy-total-pct">
            {anyBudget ? `${Math.round(totPct)}% forbrugt` : "Budget mangler"}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mb-5">
          <EcoMetric label="Kunde betalte timer" value={fmtH(totBudH)} testid="economy-total-bud-h" />
          <EcoMetric label="BSM betalte timer" value={fmtH(totRealH)} testid="economy-total-real-h" />
          <EcoMetric label="Kunde økonomi" value={fmtKr(totBudKr)} testid="economy-total-bud-kr" />
          <EcoMetric label="BSM økonomi" value={fmtKr(totRealKr)} strong testid="economy-total-real-kr" />
        </div>
        <BudgetBar pct={totPct} color={totCol.bar} />
      </div>

      {/* Pr. fase */}
      <div>
        <p className="label-caps mb-3">Pr. fase</p>
        <div className="pcard p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="economy-table">
              <thead>
                <tr className="border-b border-[#EBE8E3]">
                  <th className="py-3.5 px-5 text-left font-medium text-muted-foreground">Fase</th>
                  <th className="py-3.5 px-5 text-right label-caps">Kunde betalte timer</th>
                  <th className="py-3.5 px-5 text-right label-caps">BSM betalte timer</th>
                  <th className="py-3.5 px-5 text-right label-caps">Kunde økonomi</th>
                  <th className="py-3.5 px-5 text-right label-caps">BSM økonomi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F1EC]">
                {rows.map(({ s, bud, real, hasBudget, budKr, realKr }, idx) => {
                  const isOver = hasBudget && real > bud;
                  return (
                    <tr key={s.id} className="hover:bg-[#FBFAF8] transition-colors" data-testid={`economy-row-${idx}`}>
                      <td className="py-3 px-5 text-[#1A1919] whitespace-nowrap" data-testid={`economy-row-name-${idx}`}>
                        <span className="text-muted-foreground">{idx + 1}.</span> {s.name}
                      </td>
                      <td className="py-3 px-5 text-right" data-testid={`economy-row-budh-${idx}`}>
                        {canEdit ? (
                          <div className="flex items-center justify-end gap-1">
                            <Input
                              type="text" inputMode="decimal" className="h-8 w-20 text-right"
                              defaultValue={daNum(s.budgeted_hours)} placeholder="0"
                              onBlur={(e) => {
                                const raw = e.target.value.trim().replace(",", ".");
                                const v = raw === "" ? null : parseFloat(raw);
                                if (raw !== "" && (isNaN(v) || v < 0)) return;
                                if (v !== (s.budgeted_hours ?? null)) onSaveBudget(s.id, v);
                              }}
                              data-testid={`economy-step-budget-input-${idx}`}
                            />
                            <span className="text-muted-foreground">t</span>
                          </div>
                        ) : <span className="text-[#1A1919]">{hasBudget ? fmtH(bud) : "—"}</span>}
                      </td>
                      <td className="py-3 px-5 text-right text-[#1A1919]" data-testid={`economy-row-realh-${idx}`}>{fmtH(real)}</td>
                      <td className="py-3 px-5 text-right text-[#1A1919]" data-testid={`economy-row-budkr-${idx}`}>{hasBudget ? fmtKr(budKr) : "—"}</td>
                      <td className="py-3 px-5 text-right font-medium" style={{ color: isOver ? "#A83232" : "#1A1919" }} data-testid={`economy-row-realkr-${idx}`}>{fmtKr(realKr)}</td>
                    </tr>
                  );
                })}
                {clines.map((l, i) => {
                  const bk = Number(l.budget_kr) || 0, rk = Number(l.realized_kr) || 0;
                  const over = bk > 0 && rk > bk;
                  return (
                    <tr key={l.id || `c-${i}`} className="hover:bg-[#FBFAF8] transition-colors bg-[#FBFAF8]" data-testid={`economy-custom-row-${i}`}>
                      <td className="py-3 px-5 text-[#1A1919]">
                        <div className="flex items-center gap-1.5">
                          {canEdit ? (
                            <Input className="h-8 w-44" defaultValue={l.label} placeholder="fx Fiksturer"
                              onBlur={(e) => { if (e.target.value !== l.label) commit(i, { label: e.target.value }); }}
                              data-testid={`economy-custom-label-${i}`} />
                          ) : <span className="font-medium">{l.label || "—"}</span>}
                          {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive shrink-0" onClick={() => removeLine(i)} data-testid={`economy-custom-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                        </div>
                      </td>
                      <td className="py-3 px-5 text-right text-muted-foreground">—</td>
                      <td className="py-3 px-5 text-right text-muted-foreground">—</td>
                      <td className="py-3 px-5 text-right" data-testid={`economy-custom-budkr-${i}`}>
                        {canEdit ? (
                          <div className="flex items-center justify-end gap-1">
                            <Input type="text" inputMode="decimal" className="h-8 w-24 text-right" defaultValue={daNum(l.budget_kr)} placeholder="0"
                              onBlur={(e) => { const v = parseKr(e.target.value); if (v !== (l.budget_kr ?? null)) commit(i, { budget_kr: v }); }}
                              data-testid={`economy-custom-budkr-input-${i}`} />
                            <span className="text-muted-foreground">kr</span>
                          </div>
                        ) : <span>{bk ? fmtKr(bk) : "—"}</span>}
                      </td>
                      <td className="py-3 px-5 text-right font-medium" style={{ color: over ? "#A83232" : "#1A1919" }} data-testid={`economy-custom-realkr-${i}`}>
                        {canEdit ? (
                          <div className="flex items-center justify-end gap-1">
                            <Input type="text" inputMode="decimal" className="h-8 w-24 text-right" defaultValue={daNum(l.realized_kr)} placeholder="0"
                              onBlur={(e) => { const v = parseKr(e.target.value); if (v !== (l.realized_kr ?? null)) commit(i, { realized_kr: v }); }}
                              data-testid={`economy-custom-realkr-input-${i}`} />
                            <span className="text-muted-foreground">kr</span>
                          </div>
                        ) : <span>{fmtKr(rk)}</span>}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && clines.length === 0 && (
                  <tr><td colSpan={5} className="py-10 px-5 text-center text-muted-foreground">Ingen projekttrin endnu.</td></tr>
                )}
              </tbody>
              {(rows.length > 0 || clines.length > 0) && (
                <tfoot>
                  <tr className="border-t border-[#EBE8E3] bg-[#FBFAF8] font-semibold text-[#1A1919]" data-testid="economy-row-total">
                    <td className="py-3.5 px-5">I alt</td>
                    <td className="py-3.5 px-5 text-right" data-testid="economy-total-budh">{fmtH(totBudH)}</td>
                    <td className="py-3.5 px-5 text-right" data-testid="economy-total-realh">{fmtH(totRealH)}</td>
                    <td className="py-3.5 px-5 text-right" data-testid="economy-total-budkr">{fmtKr(totBudKr)}</td>
                    <td className="py-3.5 px-5 text-right" data-testid="economy-total-realkr">{fmtKr(totRealKr)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" className="rounded-full mt-4 border-[#EBE8E3]" onClick={addLine} data-testid="economy-add-line">
            <Plus className="h-4 w-4 mr-1.5" /> Tilføj linje
          </Button>
        )}
        <p className="mt-3 text-xs text-muted-foreground">Budget er de aftalte timer med kunden (fase-linjer), samt frie budgetlinjer i kr. Realiseret er BSM's reelle forbrug og opdateres automatisk fra "Timer brugt" på hver fase.</p>
      </div>
    </div>
  );
}


function CommentsCard({ projectId, canEdit }) {
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const load = () => api.get(`/projects/${projectId}/comments`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]);
  const dl = async (a) => {
    try {
      const r = await api.get(`/projects/${projectId}/comments/attachments/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = a.filename; link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Kunne ikke hente filen"); }
  };
  const send = async () => {
    const m = msg.trim(); if (!m && files.length === 0) return;
    setSending(true);
    try {
      const fd = new FormData(); fd.append("message", m); files.forEach((f) => fd.append("files", f));
      await api.post(`/projects/${projectId}/comments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setMsg(""); setFiles([]); await load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <div className="pcard p-6 mb-6" data-testid="comments-card">
      <h3 className="font-semibold tracking-tight mb-4 text-[#1A1919] flex items-center gap-2"><MessageSquare className="h-4 w-4 text-muted-foreground" /> Kommunikation</h3>
      <div className="space-y-3 mb-4">
        {items.length === 0 && <p className="text-sm text-muted-foreground">Ingen beskeder endnu. Kundens beskeder fra delingssiden vises her.</p>}
        {items.map((c) => (
          <div key={c.id} className={`rounded-xl border px-4 py-3 ${c.source === "bsm" ? "border-[#E9C9C3] bg-[#F1DEDA]/40" : "border-[#EBE8E3] bg-white"}`} data-testid={`comment-${c.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-[#1A1919]">{c.author_name}</span>
              <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${c.source === "bsm" ? "bg-[#F1DEDA] text-[#A83232]" : "bg-stone-100 text-stone-600"}`}>{c.source === "bsm" ? "BSM" : "Kunde"}</span>
              <span className="text-xs text-muted-foreground ml-auto">{new Date(c.created_at).toLocaleString("da-DK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
            </div>
            <p className="text-sm text-stone-700 whitespace-pre-wrap">{c.message}</p>
            {(c.attachments || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid={`comment-attachments-${c.id}`}>
                {c.attachments.map((a) => (
                  <button key={a.id} onClick={() => dl(a)} className="inline-flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 border border-[#E6DDD6] bg-[#F4EFEC] text-[#5C4A42] hover:shadow-sm transition-all" data-testid={`comment-attachment-${a.id}`}>
                    <FileText className="h-3.5 w-3.5 text-[#806B61]" /> <span className="max-w-[160px] truncate font-medium">{a.filename}</span> <Download className="h-3 w-3 text-stone-400" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-2">
        {canEdit ? (
          <>
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder="Skriv et svar til kunden…" className="w-full rounded-xl border border-[#EBE8E3] px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#A83232]/30" data-testid="comment-input" />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="comment-pending-files">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 border border-[#E6DDD6] bg-[#F4EFEC] text-[#5C4A42]"><FileText className="h-3.5 w-3.5 text-[#806B61]" /><span className="max-w-[160px] truncate">{f.name}</span><button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-stone-400 hover:text-[#A83232]" aria-label="Fjern fil" data-testid={`comment-remove-file-${i}`}><X className="h-3 w-3" /></button></span>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between">
              <label className="inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer rounded-full px-3 py-1.5 transition-colors hover:bg-[#F4EFEC] text-[#806B61]" data-testid="comment-attach-label">
                <Paperclip className="h-3.5 w-3.5" /> Vedhæft fil
                <input type="file" multiple className="hidden" onChange={(e) => { setFiles([...files, ...Array.from(e.target.files)]); e.target.value = ""; }} data-testid="comment-file-input" />
              </label>
              <Button size="sm" className="rounded-full font-medium bg-[#A83232] hover:bg-[#8f2a2a]" disabled={sending || (!msg.trim() && files.length === 0)} onClick={send} data-testid="comment-send"><MessageSquare className="h-4 w-4 mr-1.5" /> Send svar</Button>
            </div>
          </>
        ) : (
          <p className="text-xs text-stone-400" data-testid="comment-readonly">Skrivebeskyttet — kun projektets ejer kan svare kunden.</p>
        )}
      </div>
    </div>
  );
}

function MinutesCard({ projectId, canEdit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", date: "", customer_attendees: "", notes: "", visible_to_customer: false });
  const [bsmIds, setBsmIds] = useState([]);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);

  const load = () => api.get(`/projects/${projectId}/minutes`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.active !== false))).catch(() => {}); /* eslint-disable-next-line */ }, [projectId]);

  const openNew = () => { setEditing(null); setForm({ title: "", date: new Date().toISOString().slice(0, 10), customer_attendees: "", notes: "", visible_to_customer: false }); setBsmIds([]); setActions([]); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ title: m.title || "", date: (m.date || "").slice(0, 10), customer_attendees: m.customer_attendees || "", notes: m.notes || "", visible_to_customer: !!m.visible_to_customer }); setBsmIds(m.bsm_attendee_ids || []); setActions((m.action_items || []).map((a) => (typeof a === "string" ? a : a.text || ""))); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Angiv en titel"); return; }
    setSaving(true);
    const payload = { ...form, bsm_attendee_ids: bsmIds, action_items: actions.filter((a) => (a || "").trim()) };
    try {
      if (editing) await api.patch(`/project-minutes/${editing.id}`, payload);
      else await api.post(`/projects/${projectId}/minutes`, payload);
      setOpen(false); await load();
      toast.success(editing ? "Referat opdateret" : "Referat oprettet");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const doDelete = async () => { try { await api.delete(`/project-minutes/${delId}`); setDelId(null); await load(); toast.success("Referat slettet"); } catch (e) { toast.error("Kunne ikke slette"); } };
  const userOptions = users.map((u) => ({ value: u.id, label: u.name }));

  return (
    <div data-testid="minutes-card">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h3 className="text-xl font-semibold tracking-tight text-[#1A1919] flex items-center gap-2"><NotebookText className="h-5 w-5 text-[#A83232]" /> Mødereferater</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Referater fra møder — beslutninger, deltagere og action-punkter.</p>
        </div>
        {canEdit && <Button onClick={openNew} className="rounded-full font-medium bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="minute-new-btn"><Plus className="h-4 w-4 mr-1.5" /> Nyt referat</Button>}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Henter…</p>
      ) : items.length === 0 ? (
        <div className="pcard p-10 text-center" data-testid="minutes-empty">
          <div className="h-12 w-12 rounded-2xl bg-[#F3F1EC] flex items-center justify-center mx-auto mb-3"><NotebookText className="h-5 w-5 text-stone-400" /></div>
          <p className="font-medium text-[#1A1919]">Ingen mødereferater endnu</p>
          <p className="text-sm text-muted-foreground mt-1">{canEdit ? "Opret det første referat med knappen ovenfor." : "Der er endnu ikke oprettet referater."}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((m) => {
            return (
              <div key={m.id} className="pcard p-6" data-testid={`minute-${m.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-[#1A1919] text-lg">{m.title}</h4>
                      {m.visible_to_customer && <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#E7EADF] text-[#7E856C] border border-[#D3D8C6]"><Eye className="h-3 w-3" /> Synlig for kunde</span>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2"><CalendarClock className="h-3.5 w-3.5" /> {daDate(m.date)}{m.author_name ? ` · ${m.author_name}` : ""}</p>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-stone-500" onClick={() => openEdit(m)} data-testid={`minute-edit-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDelId(m.id)} data-testid={`minute-delete-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>

                {(m.bsm_attendees || []).length > 0 && (
                  <div className="mt-4">
                    <p className="label-caps mb-1.5 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Deltagere fra BSM</p>
                    <div className="flex flex-wrap gap-1.5">{(m.bsm_attendees || []).map((u) => <RespChip key={u.id} user={u} />)}</div>
                  </div>
                )}

                {m.customer_attendees && (
                  <div className="mt-4">
                    <p className="label-caps mb-1.5">Deltagere fra kunde</p>
                    <p className="text-sm text-stone-700">{m.customer_attendees}</p>
                  </div>
                )}

                {m.notes && <div className="mt-4"><p className="label-caps mb-1.5">Referat</p><p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{m.notes}</p></div>}

                {(m.action_items || []).length > 0 && (
                  <div className="mt-4">
                    <p className="label-caps mb-1.5">Action-punkter</p>
                    <ul className="space-y-1.5 list-disc pl-5">
                      {(m.action_items || []).map((a, i) => (
                        <li key={i} className="text-sm text-stone-700" data-testid={`minute-${m.id}-action-${i}`}>{typeof a === "string" ? a : a.text}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="minute-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Rediger referat" : "Nyt mødereferat"}</DialogTitle>
            <DialogDescription>Udfyld referatet — det gemmes på projektet.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div><Label className="label-caps">Titel</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="fx Opstartsmøde" className="mt-1.5 bg-white" data-testid="minute-title" /></div>
            <div><Label className="label-caps">Dato</Label><div className="mt-1.5"><DanishDatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} allowClosed testid="minute-date" /></div></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="label-caps">Deltagere fra BSM</Label><div className="mt-1.5"><MultiSelect options={userOptions} selected={bsmIds} onChange={setBsmIds} placeholder="Vælg en eller flere" testid="minute-bsm" /></div></div>
              <div><Label className="label-caps">Deltagere fra kunde</Label><Input value={form.customer_attendees} onChange={(e) => setForm({ ...form, customer_attendees: e.target.value })} placeholder="fx Lone Bødsgaard" className="mt-1.5 bg-white" data-testid="minute-customer" /></div>
            </div>
            <div><Label className="label-caps">Referat / beslutninger</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={5} placeholder="Kort opsummering af mødet…" className="mt-1.5 bg-white" data-testid="minute-notes" /></div>
            <div>
              <div className="flex items-center justify-between mb-1.5"><Label className="label-caps">Action-punkter</Label><Button size="sm" variant="outline" className="rounded-full h-7 border-[#EBE8E3]" onClick={() => setActions([...actions, ""])} data-testid="minute-add-action"><Plus className="h-3.5 w-3.5 mr-1" /> Tilføj punkt</Button></div>
              <div className="space-y-2">
                {actions.length === 0 && <p className="text-xs text-muted-foreground">Ingen punkter endnu.</p>}
                {actions.map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={a} onChange={(e) => setActions(actions.map((x, ix) => (ix === i ? e.target.value : x)))} placeholder={`Punkt ${i + 1}`} className="h-9 bg-white" data-testid={`minute-action-input-${i}`} />
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-stone-400 hover:text-destructive shrink-0" onClick={() => setActions(actions.filter((_, ix) => ix !== i))} data-testid={`minute-action-remove-${i}`}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
            <label className="flex items-center justify-between rounded-xl border border-[#EBE8E3] px-4 py-3 cursor-pointer">
              <span className="text-sm"><span className="font-medium text-[#1A1919]">Synlig for kunde</span><span className="block text-xs text-muted-foreground">Vises på kundens delingsside</span></span>
              <Switch checked={form.visible_to_customer} onCheckedChange={(c) => setForm({ ...form, visible_to_customer: c })} data-testid="minute-visible" />
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="rounded-full border-[#EBE8E3]" onClick={() => setOpen(false)}>Annullér</Button>
            <Button className="rounded-full font-medium bg-[#A83232] hover:bg-[#8f2a2a]" disabled={saving} onClick={save} data-testid="minute-save">{saving ? "Gemmer…" : (editing ? "Gem" : "Opret referat")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Slet referat?</AlertDialogTitle><AlertDialogDescription>Handlingen kan ikke fortrydes.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-full">Annullér</AlertDialogCancel><AlertDialogAction className="rounded-full bg-[#A83232] hover:bg-[#8f2a2a]" onClick={doDelete} data-testid="minute-delete-confirm">Slet</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


const CASE_TYPE_LABEL = { kvalitet: "Kvalitet", indkoeb: "Indkøb" };
const CASE_STATUS_BADGE = {
  "Ny": "bg-stone-50 text-stone-500 border-stone-200", "I gang": "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]",
  "Afventer svar": "bg-[#F9F1D9] text-[#8A6E17] border-[#ECE0B6]", "Færdig": "bg-[#E7EADF] text-[#7E856C] border-[#D3D8C6]",
};

const CASE_DEFAULT_RESP = { indkoeb: "Anna Fuhr", kvalitet: "Tom Sørensen" };
function ProjectCases({ projectId, customerId, customerName, canEdit }) {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const empty = { type: "", title: "", priority: "Normal", due_date: "", work_days: "", description: "", responsible_id: null };
  const [f, setF] = useState(empty);
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);
  const [caseToDelete, setCaseToDelete] = useState(null);
  const delCase = async () => { try { await api.delete(`/cases/${caseToDelete.id}`); toast.success("Sag slettet"); setCaseToDelete(null); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Kunne ikke slette sag"); } };
  const load = () => api.get(`/cases?project_id=${projectId}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([])); }, []);
  const deptUsers = (users || []).filter((u) => u.active !== false && (u.role === "admin" || (u.departments || []).includes(f.type)));
  const reset = () => { setF(empty); setFiles([]); };
  const computedStart = (() => {
    const d = f.work_days ? parseInt(f.work_days, 10) : 0;
    if (!f.due_date || !d || d < 1) return null;
    const [y, m, dd] = f.due_date.slice(0, 10).split("-").map(Number);
    const dt = new Date(y, m - 1, dd); let rem = d - 1;
    while (rem > 0) { dt.setDate(dt.getDate() - 1); if (dt.getDay() !== 0 && dt.getDay() !== 6) rem -= 1; }
    return `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`;
  })();
  const submit = async () => {
    if (!f.type) return toast.error("Vælg en afdeling");
    if (!f.title.trim()) return toast.error("Angiv en titel");
    setSaving(true);
    try {
      const r = await api.post("/cases", { type: f.type, title: f.title, priority: f.priority, due_date: f.due_date || null, work_days: f.work_days ? parseInt(f.work_days, 10) : null, description: f.description, source_project_id: projectId, customer_id: customerId || null, assigned_user_ids: f.responsible_id ? [f.responsible_id] : [] });
      const cid = r.data.id;
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/cases/${cid}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Sagen er sendt til afdelingen");
      setOpen(false); reset(); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Kunne ikke sende sag"); }
    setSaving(false);
  };
  return (
    <div className="pcard p-6 mb-6" data-testid="project-cases-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold tracking-tight text-[#1A1919]">Interne sager</h3>
        {canEdit && <Button size="sm" className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="send-case-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Send til afdeling</Button>}
      </div>
      <p className="text-xs text-stone-400 mb-4">Kun synligt internt — ikke for kunden.</p>
      {items.length === 0 ? (
        <p className="text-sm text-stone-400">Ingen sager sendt endnu.</p>
      ) : (
        <div className="space-y-2.5">
          {items.map((c) => {
            const ans = (c.assigned_users && c.assigned_users[0]?.name) || "—";
            return (
            <div key={c.id} className="rounded-xl border border-[#F3F1EC] px-4 py-3.5 hover:border-[#A83232]/40 [transition-property:border-color] duration-200" data-testid={`project-case-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-400">{CASE_TYPE_LABEL[c.type]}</span>
                  <Link to={`/sag/${c.id}`} className="block text-sm font-semibold text-stone-800 truncate hover:text-[#A83232] [transition-property:color] duration-200">{c.title}</Link>
                </div>
                <span className="flex items-center gap-2 shrink-0">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${CASE_STATUS_BADGE[c.status] || ""}`}>{c.status}</span>
                  {canEdit && <button type="button" onClick={() => setCaseToDelete(c)} data-testid={`project-case-del-${c.id}`} className="text-stone-300 hover:text-destructive [transition-property:color] duration-200"><Trash2 className="h-4 w-4" /></button>}
                  <Link to={`/sag/${c.id}`} data-testid={`project-case-open-${c.id}`} className="text-[#A83232]"><ExternalLink className="h-4 w-4" /></Link>
                </span>
              </div>
              <div className="mt-3 pt-3 border-t border-[#F3F1EC] grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
                <div className="flex items-center justify-between gap-2 sm:block"><span className="text-stone-400 sm:block sm:mb-0.5">Deadline</span><span className="text-stone-700 font-medium">{c.due_date ? daDate(c.due_date) : "Ikke fastlagt"}</span></div>
                <div className="flex items-center justify-between gap-2 sm:block"><span className="text-stone-400 sm:block sm:mb-0.5">Ansvarlig</span><span className="text-stone-700 font-medium truncate">{ans}</span></div>
                <div className="flex items-center justify-between gap-2 sm:block"><span className="text-stone-400 sm:block sm:mb-0.5">Sendt af</span><span className="text-stone-600 truncate">{c.created_by_name || "—"}</span></div>
              </div>
            </div>
            );
          })}
        </div>
      )}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="rounded-2xl sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="send-case-dialog">
          <DialogHeader><DialogTitle className="text-xl font-semibold">Send sag til afdeling</DialogTitle><DialogDescription className="text-sm text-stone-500">Relationen til dette projekt oprettes automatisk.</DialogDescription></DialogHeader>
          <div className="space-y-5 mt-1">
            <div className="space-y-1.5"><Label className="text-sm font-medium">Afdeling *</Label>
              <Select value={f.type} onValueChange={(v) => setF((p) => ({ ...p, type: v, responsible_id: users.find((u) => u.name === CASE_DEFAULT_RESP[v])?.id || null }))}>
                <SelectTrigger data-testid="send-case-type" className="rounded-xl h-11 bg-white"><SelectValue placeholder="Vælg afdeling" /></SelectTrigger>
                <SelectContent><SelectItem value="kvalitet">Kvalitet</SelectItem><SelectItem value="indkoeb">Indkøb</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-sm font-medium">Titel på sag *</Label><Input data-testid="send-case-title" value={f.title} onChange={(e) => setF((p) => ({ ...p, title: e.target.value }))} className="rounded-xl h-11 bg-white" placeholder="Fx Bestil 4 ton stål" /></div>
            <div className="space-y-1.5"><Label className="text-sm font-medium">Beskrivelse</Label><Textarea data-testid="send-case-description" value={f.description} onChange={(e) => setF((p) => ({ ...p, description: e.target.value }))} rows={4} className="rounded-xl bg-white resize-none" placeholder="Beskriv hvad afdelingen skal løse…" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-sm font-medium">Prioritet</Label>
                <Select value={f.priority} onValueChange={(v) => setF((p) => ({ ...p, priority: v }))}>
                  <SelectTrigger data-testid="send-case-priority" className="rounded-xl h-11 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Lav", "Normal", "Høj"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-sm font-medium">Deadline</Label><DanishDatePicker value={f.due_date || ""} onChange={(v) => setF((p) => ({ ...p, due_date: v }))} testid="send-case-due" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-sm font-medium">Antal arbejdsdage</Label><Input type="number" min="1" data-testid="send-case-workdays" value={f.work_days} onChange={(e) => setF((p) => ({ ...p, work_days: e.target.value }))} className="rounded-xl h-11 bg-white" placeholder="Fx 5" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-medium">Beregnet startdato</Label>
                <div className="rounded-xl h-11 bg-[#FBFAF8] border border-[#EBE8E3] flex items-center px-3 text-sm" data-testid="send-case-computed-start">
                  {computedStart ? <span className="font-semibold text-stone-700">{computedStart}</span> : <span className="text-stone-400">Udfyld deadline + arbejdsdage</span>}
                </div>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-sm font-medium">Kunde</Label>
              <div className="flex items-center gap-2.5 rounded-xl h-11 bg-[#FBFAF8] border border-[#EBE8E3] px-3" data-testid="send-case-customer">
                <Building2 className="h-4 w-4 text-stone-400 shrink-0" strokeWidth={1.75} />
                <span className="text-sm text-stone-700 truncate">{customerName || "Ingen kunde på projektet"}</span>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-sm font-medium">Sagsansvarlig *</Label>
              <UserSingleSelect users={deptUsers} value={f.responsible_id} onChange={(v) => setF((p) => ({ ...p, responsible_id: v }))} testid="send-case-responsible" className="h-11" placeholder="Vælg ansvarlig" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Dokumentation</Label>
              <input ref={fileRef} type="file" multiple className="hidden" data-testid="send-case-file-input" onChange={(e) => { setFiles((p) => [...p, ...Array.from(e.target.files || [])]); e.target.value = ""; }} />
              {files.map((file, i) => (
                <div key={i} className="flex items-center gap-2 rounded-xl border border-[#F3F1EC] px-3 py-2 bg-white" data-testid={`send-case-file-${i}`}>
                  <FileText className="h-4 w-4 text-stone-400 shrink-0" />
                  <span className="text-sm text-stone-700 truncate flex-1">{file.name}</span>
                  <button type="button" onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))} className="text-stone-300 hover:text-destructive shrink-0"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="send-case-add-file" className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#D6D3D1] text-stone-500 hover:text-[#A83232] hover:border-[#A83232] py-2 text-sm font-medium"><Upload className="h-4 w-4" /> Vedhæft fil</button>
            </div>
            <div className="space-y-1.5"><Label className="text-sm font-medium">Sendes af</Label>
              <div className="flex items-center gap-2.5 rounded-xl h-11 bg-[#FBFAF8] border border-[#EBE8E3] px-3" data-testid="send-case-sender">
                <span className="h-7 w-7 rounded-full bg-[#F1DEDA] text-[#A83232] text-[11px] font-semibold flex items-center justify-center shrink-0">{(me?.name || "").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase()).join("") || "?"}</span>
                <span className="text-sm text-stone-700">{me?.name || "—"}</span>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="rounded-xl border-[#EBE8E3]" onClick={() => setOpen(false)}>Annullér</Button>
            <Button className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" onClick={submit} disabled={saving} data-testid="send-case-submit">{saving ? "Sender…" : "Send sag"}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!caseToDelete} onOpenChange={(o) => !o && setCaseToDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader><AlertDialogTitle className="text-xl font-medium">Slet sag?</AlertDialogTitle>
            <AlertDialogDescription>Sagen "{caseToDelete?.title}" slettes permanent. Dette kan ikke fortrydes.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annullér</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" onClick={delCase} data-testid="confirm-delete-project-case">Slet sag</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function ProjectDetail() {  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "steps");
  const { user: me } = useAuth();
  const [p, setP] = useState(null);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [files, setFiles] = useState([]);
  const [openStep, setOpenStep] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [deadlineReason, setDeadlineReason] = useState("");

  const loadFiles = () => api.get(`/projects/${id}/files`).then((r) => setFiles(r.data)).catch(() => {});
  const loadProject = () => api.get(`/projects/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { loadProject(); loadFiles(); api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active !== false))); api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}); api.post(`/projects/${id}/status-events/seen`).catch(() => {}); }, [id]);

  if (!p) return <p className="text-muted-foreground">Indlæser…</p>;
  const isAdmin = me.role === "admin";
  const canEdit = !!p.can_edit;
  const canEditContact = canEdit && (isAdmin || me.role === "projektleder");
  const steps = [...(p.steps || [])].sort((a, b) => a.sort_order - b.sort_order);
  const custContacts = customers.find((c) => c.id === p.customer_id)?.contacts || [];
  const ccIndex = p.customer_contact ? custContacts.findIndex((c) => c.name === p.customer_contact.name && (c.email || "") === (p.customer_contact.email || "")) : -1;
  const setCustomerContact = async (val) => {
    const contact = val === NONE ? null : (custContacts[parseInt(val)] || null);
    try { const { data } = await api.patch(`/projects/${id}/customer-contact`, { contact }); setP(data); toast.success("Kontaktperson opdateret"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const managers = users.filter((u) => u.role === "admin" || (u.role === "projektleder" && (u.departments || []).includes("salg"))).sort((a, b) => (a.name || "").localeCompare(b.name || "", "da", { sensitivity: "base" }));
  const salgUsers = users.filter((u) => u.active !== false && (u.role === "admin" || (u.departments || []).includes("salg")));
  const setManager = async (val) => {
    try { const { data } = await api.patch(`/projects/${id}/manager`, { project_manager_id: val === NONE ? null : val }); setP(data); toast.success("Projektansvarlig opdateret"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const patchStep = async (sid, patch) => {
    try { const { data } = await api.patch(`/project-steps/${sid}`, patch); setP(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const applyStatus = async (status, preserve) => {
    try {
      const body = { status };
      if (preserve !== undefined) body.preserve_deadline_history = preserve;
      const { data } = await api.patch(`/projects/${id}/status`, body); setP(data); toast.success("Status opdateret");
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const changeStatus = (status) => {
    if (status === "aktiv" && !p.start_date) { setPendingAction({ type: "activate_no_deadline" }); return; }
    if (status === "ikke_igangsat" && (p.deadline_history || []).length > 0) { setPendingAction({ type: "deadline_history_choice" }); return; }
    applyStatus(status);
  };
  const setSchedule = async (deadline, reason) => {
    const { data } = await api.patch(`/projects/${id}/schedule`, { deadline, reason }); setP(data); return data;
  };
  const onPickDeadline = async (dateStr) => {
    const wasNotStarted = p.status === "ikke_igangsat";
    const hadDeadline = !!p.expected_end_date;
    // Kun aktive/afventende projekter kræver begrundelse ved deadlineændring.
    if (!wasNotStarted && dateStr && hadDeadline && dateStr !== String(p.expected_end_date).slice(0, 10)) {
      setDeadlineReason(""); setPendingAction({ type: "deadline_reason", date: dateStr }); return;
    }
    try { await setSchedule(dateStr, null); toast.success("Deadline opdateret"); if (dateStr && wasNotStarted && !hadDeadline) setPendingAction({ type: "deadline_added" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirmDeadlineChange = async () => {
    const r = (deadlineReason || "").trim();
    if (!r) { toast.error("Angiv en årsag"); return; }
    try { await setSchedule(pendingAction.date, r); toast.success("Deadline opdateret"); setPendingAction(null); setDeadlineReason(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const clearDeadline = async () => {
    const wasActive = p.status === "aktiv";
    try { await setSchedule(null, null); toast.success("Deadline fjernet"); if (wasActive) setPendingAction({ type: "deadline_removed" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const shareUrl = p.share_token ? `${window.location.origin}/kunde/${p.share_token}` : null;
  const createShare = async () => {
    try { const { data } = await api.post(`/projects/${id}/share`); setP({ ...p, share_token: data.share_token }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const regenShare = async () => {
    try { const { data } = await api.post(`/projects/${id}/share/regenerate`); setP({ ...p, share_token: data.share_token }); toast.success("Nyt link genereret"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const disableShare = async () => {
    try { await api.delete(`/projects/${id}/share`); setP({ ...p, share_token: null }); toast.success("Link deaktiveret"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link kopieret"); }
    catch (e) { toast.error("Kunne ikke kopiere automatisk — markér linket og kopiér manuelt"); }
  };
  const ss = p.share_settings || { description: true, progress: true, phases: true, responsibles: true, deadlines: true, documents: true, contact: true, economy: true };
  const setShare = async (key, val) => {
    setP({ ...p, share_settings: { ...ss, [key]: val } });
    try { await api.patch(`/projects/${id}/share/settings`, { [key]: val }); }
    catch (e) { toast.error("Kunne ikke opdatere delingsindstilling"); }
  };
  const move = async (idx, dir) => {
    const ids = steps.map((s) => s.id); const j = idx + dir; if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    const { data } = await api.post(`/projects/${id}/steps/reorder`, { ordered_ids: ids }); setP(data);
  };
  const addStep = async () => {
    const { data } = await api.post(`/projects/${id}/steps`, { name: "Nyt trin", duration_days: 1, selected: true });
    setP(data); toast.success("Trin tilføjet");
  };
  const delStep = async (sid) => { const { data } = await api.delete(`/project-steps/${sid}`); setP(data); toast.success("Trin fjernet"); };
  const saveBudget = async (sid, v) => {
    try { const { data } = await api.patch(`/project-steps/${sid}`, { budgeted_hours: v }); setP(data); toast.success("Budget gemt"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveEconomyLines = async (lines) => {
    try { const { data } = await api.patch(`/projects/${id}/economy-lines`, { lines }); setP(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const stepFileCount = (sid) => files.filter((f) => f.step_id === sid).length;
  const sMeta = statusMeta[p.status] || statusMeta.ikke_igangsat;
  const tabTrigger = "rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-0 text-sm font-medium text-muted-foreground shadow-none data-[state=active]:bg-transparent data-[state=active]:text-[#1A1919] data-[state=active]:shadow-none data-[state=active]:border-[#A83232] transition-colors";

  return (
    <div className="rise-in pb-16">
      {/* Sticky glass header */}
      <div className="lg:sticky lg:top-0 z-30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 mb-8 backdrop-blur-xl bg-[#FBFAF8]/85 border-b border-[#EBE8E3]">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="mb-3 -ml-2 text-muted-foreground" data-testid="back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Tilbage til oversigt</Button>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="label-caps">{p.template_name || "Projekt"}</p>
            <h1 className="text-3xl sm:text-4xl font-light tracking-tight mt-1.5 text-[#1A1919] truncate" data-testid="project-title">{p.name}</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground mr-1"><span className={`h-2 w-2 rounded-full ${sMeta.dot}`} /> {sMeta.label}</span>
            {canEdit && <Button size="sm" className="rounded-full bg-[#A83232] hover:bg-[#8f2a2a] text-white shadow-sm active:scale-95 transition-transform" onClick={() => setShareOpen(true)} data-testid="share-project-btn"><Share2 className="h-4 w-4 mr-2" /> Del med kunde</Button>}
          </div>
        </div>
        {canEditContact && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="status-switcher">
            {["ikke_igangsat", "aktiv", "paused", "afsluttet"].map((s) => (
              <Button key={s} size="sm" variant={p.status === s ? "default" : "outline"} className={`rounded-full text-xs ${p.status === s ? "bg-[#A83232] hover:bg-[#8f2a2a] border-transparent" : "border-[#EBE8E3]"}`} onClick={() => changeStatus(s)} data-testid={`status-${s}-btn`}>{statusMeta[s].label}</Button>
            ))}
          </div>
        )}
      </div>

      {p.description && <p className="text-muted-foreground mb-8 max-w-2xl leading-relaxed">{p.description}</p>}

      {/* Bento grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Team & kunde */}
        <div className="pcard p-6 space-y-4" data-testid="bento-team">
          <p className="label-caps flex items-center gap-2"><Users className="h-3.5 w-3.5" /> Team & kunde</p>
          <div className="space-y-3">
            <div className="flex items-start gap-2.5"><Building2 className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" /><div><p className="text-xs text-muted-foreground">Kunde</p><p className="font-medium text-[#1A1919]">{p.customer_name || "—"}</p></div></div>
            <div className="flex items-start gap-2.5"><UserIcon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" /><div className="min-w-0 flex-1"><p className="text-xs text-muted-foreground">Projektansvarlig</p>
              {canEditContact ? (
                <Select value={p.project_manager_id || NONE} onValueChange={setManager}>
                  <SelectTrigger className="h-9 rounded-xl mt-0.5" data-testid="project-manager-select"><SelectValue placeholder="Vælg ansvarlig" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Ingen valgt</SelectItem>
                    {managers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : <p className="font-medium text-[#1A1919]">{p.project_manager_name || "—"}</p>}
            </div></div>
          </div>
          <div className="border-t border-[#EBE8E3] pt-3" data-testid="project-contact-card">
            <p className="text-xs text-muted-foreground mb-1.5">Kundens kontaktperson</p>
            {canEditContact && custContacts.length > 0 ? (
              <Select value={ccIndex >= 0 ? String(ccIndex) : NONE} onValueChange={setCustomerContact}>
                <SelectTrigger className="h-9 rounded-xl" data-testid="project-contact-select"><SelectValue placeholder="Vælg kontaktperson" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Ingen valgt</SelectItem>
                  {custContacts.map((c, i) => <SelectItem key={i} value={String(i)}>{c.name || c.email || `Kontakt ${i + 1}`}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : null}
            {p.customer_contact ? (
              <div className={canEditContact && custContacts.length > 0 ? "mt-2 text-sm" : "text-sm"} data-testid="project-contact-display">
                {!(canEditContact && custContacts.length > 0) && <p className="font-medium text-[#1A1919]">{p.customer_contact.name || "—"}</p>}
                {p.customer_contact.email && <p className="text-stone-500 mt-0.5">{p.customer_contact.email}</p>}
                {p.customer_contact.phone && <p className="text-stone-500 mt-0.5">{p.customer_contact.phone}</p>}
              </div>
            ) : (!(canEditContact && custContacts.length > 0) && <p className="text-sm text-muted-foreground">Ingen kontaktperson valgt</p>)}
          </div>
        </div>

        {/* Tidslinje & status */}
        <div className="pcard p-6 space-y-4" data-testid="bento-timeline">
          <p className="label-caps flex items-center gap-2"><CalendarClock className="h-3.5 w-3.5" /> Tidslinje & status</p>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Deadline</p>
            {canEditContact ? (
              <div className="flex items-center gap-2">
                <div className="w-44"><DanishDatePicker value={p.expected_end_date ? String(p.expected_end_date).slice(0, 10) : ""} onChange={(iso) => onPickDeadline(iso)} testid="deadline-input" /></div>
                {p.expected_end_date && <button onClick={clearDeadline} data-testid="clear-deadline-btn" className="text-xs text-destructive font-medium">Fjern</button>}
              </div>
            ) : <p className="font-medium text-[#1A1919]">{p.expected_end_date ? daDate(p.expected_end_date) : "Ikke fastlagt"}</p>}
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Beregnet startdato</p>
            <p className="font-medium text-[#1A1919]" data-testid="computed-start">{p.start_date ? daDate(p.start_date) : "Beregnes ud fra deadline + arbejdsdage"}</p>
          </div>
          {p.deadline_reason && (
            <div className="text-xs text-stone-500 bg-[#FBFAF8] border border-[#EBE8E3] rounded-xl px-3 py-2 space-y-1.5" data-testid="deadline-reason-note">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-stone-600">Seneste deadline-ændring</span>
                {(p.deadline_history || []).length > 1 && <span className="inline-flex items-center gap-1 rounded-full bg-[#F1DEDA] text-[#A83232] px-2 py-0.5 font-semibold" data-testid="deadline-moved-count">Rykket {p.deadline_history.length} gange</span>}
              </div>
              <p>{p.deadline_reason}</p>
              {(p.deadline_history || []).length > 1 && (
                <div className="pt-1.5 mt-1 border-t border-[#EBE8E3] space-y-1" data-testid="deadline-history-internal">
                  {p.deadline_history.map((h, i) => (
                    <div key={i} data-testid={`deadline-history-i-${i}`}>
                      <p className="text-[11px] text-stone-400"><span style={{ textDecoration: "line-through" }}>{daDate(h.from)}</span> → <span className="text-stone-600 font-medium">{daDate(h.to)}</span>{h.reason ? ` · ${h.reason}` : ""}</p>
                      {(h.by || h.at) && <p className="text-[10px] text-stone-400">{h.by ? `Ændret af ${h.by}` : ""}{h.by && h.at ? " · " : ""}{h.at ? daDate(h.at) : ""}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {!p.expected_end_date && (
            <div className="text-xs text-[#806B61] flex items-start gap-1.5 bg-[#F4EFEC] border border-[#E6DDD6] rounded-xl px-3 py-2" data-testid="no-deadline-warning">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={1.75} /> <span>{p.status === "aktiv" ? "Projektet er Aktiv, men der er ikke angivet en deadline." : "Deadline er endnu ikke fastlagt."}</span>
            </div>
          )}
        </div>

        {/* Fremdrift */}
        <div className="pcard p-6" data-testid="bento-progress">
          <p className="label-caps flex items-center gap-2 mb-4"><GanttChartSquare className="h-3.5 w-3.5" /> Fremdrift</p>
          <div className="flex items-center gap-5">
            <CircularProgress value={p.progress} />
            <div className="space-y-2 text-sm">
              <p className="text-muted-foreground">{p.done_tasks}/{p.total_tasks} trin færdige</p>
              <p className="text-muted-foreground">Varighed: <strong className="text-[#1A1919]" data-testid="detail-total-duration">{p.total_duration_days || 0} dage</strong></p>
              <p className="text-muted-foreground">Tid: <strong className="text-[#1A1919]" data-testid="detail-total-hours">{p.total_hours || 0} t</strong></p>
            </div>
          </div>
          {p.delayed_tasks > 0 && <p className="flex items-center gap-1.5 text-[#A83232] font-semibold text-sm mt-4"><AlertTriangle className="h-4 w-4" /> {p.delayed_tasks} forsinkede trin</p>}
        </div>
      </div>

      <AlertDialog open={pendingAction?.type === "activate_no_deadline"} onOpenChange={(o) => !o && setPendingAction(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader><AlertDialogTitle className="text-xl font-medium">Aktivér uden deadline?</AlertDialogTitle>
            <AlertDialogDescription>Projektet har ingen deadline. Vil du stadig aktivere projektet?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annuller</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl" onClick={() => { applyStatus("aktiv"); setPendingAction(null); }} data-testid="confirm-activate-no-deadline">Ja, aktivér uden deadline</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingAction?.type === "deadline_added"} onOpenChange={(o) => !o && setPendingAction(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader><AlertDialogTitle className="text-xl font-medium">Aktivér projektet?</AlertDialogTitle>
            <AlertDialogDescription>Der er nu angivet en deadline. Skal projektets status ændres til Aktiv?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Nej, behold Ikke igangsat</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl" onClick={() => { applyStatus("aktiv"); setPendingAction(null); }} data-testid="confirm-deadline-added">Ja, sæt projektet til Aktiv</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingAction?.type === "deadline_removed"} onOpenChange={(o) => !o && setPendingAction(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader><AlertDialogTitle className="text-xl font-medium">Deadline fjernet</AlertDialogTitle>
            <AlertDialogDescription>Projektet har status Aktiv, men der er ikke længere angivet en deadline. Vil du ændre status til "Ikke igangsat"?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Behold Aktiv</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl" onClick={() => { applyStatus("ikke_igangsat"); setPendingAction(null); }} data-testid="confirm-deadline-removed">Sæt til Ikke igangsat</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingAction?.type === "deadline_history_choice"} onOpenChange={(o) => !o && setPendingAction(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="deadline-history-choice-dialog">
          <AlertDialogHeader><AlertDialogTitle className="text-xl font-medium">Hvad skal der ske med deadline-historikken?</AlertDialogTitle>
            <AlertDialogDescription>Projektet sættes tilbage til <span className="font-semibold">Ikke igangsat</span> og har tidligere registreret deadline-historik. Vælg hvordan den skal håndteres:</AlertDialogDescription></AlertDialogHeader>
          <div className="space-y-2.5 text-sm text-stone-600">
            <div className="rounded-xl border border-[#EBE8E3] bg-[#FBFAF8] px-3.5 py-2.5"><span className="font-semibold text-stone-700">Behold historik:</span> Tidligere deadlineændringer og begrundelser bevares som historiske hændelser.</div>
            <div className="rounded-xl border border-[#EBE8E3] bg-[#FBFAF8] px-3.5 py-2.5"><span className="font-semibold text-stone-700">Slet deadline-historik:</span> Den deadline-relaterede historik fjernes, fordi den tidligere ændring var en korrektion/fejl og ikke en reel forsinkelse.</div>
          </div>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="rounded-xl mt-0" data-testid="deadline-history-cancel">Annullér</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-white text-[#A83232] border border-[#E9C9C3] hover:bg-[#F1DEDA]" onClick={(e) => { e.preventDefault(); applyStatus("ikke_igangsat", false); setPendingAction(null); }} data-testid="deadline-history-delete">Slet deadline-historik</AlertDialogAction>
            <AlertDialogAction className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" onClick={(e) => { e.preventDefault(); applyStatus("ikke_igangsat", true); setPendingAction(null); }} data-testid="deadline-history-keep">Behold historik</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingAction?.type === "deadline_reason"} onOpenChange={(o) => { if (!o) { setPendingAction(null); setDeadlineReason(""); } }}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader><AlertDialogTitle className="text-xl font-medium">Årsag til ændret deadline</AlertDialogTitle>
            <AlertDialogDescription>Deadline ændres til {pendingAction?.date ? daDate(pendingAction.date) : ""}. Angiv en årsag — den vises for kunden på delingssiden.</AlertDialogDescription></AlertDialogHeader>
          <Textarea autoFocus value={deadlineReason} onChange={(e) => setDeadlineReason(e.target.value)} rows={3} className="rounded-xl bg-white resize-none" placeholder="Fx: Forsinket materialelevering fra underleverandør" data-testid="deadline-reason-input" />
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annullér</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" onClick={(e) => { e.preventDefault(); confirmDeadlineChange(); }} data-testid="confirm-deadline-reason">Gem ændring</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto w-full justify-start gap-6 rounded-none border-b border-[#EBE8E3] bg-transparent p-0">
          <TabsTrigger value="steps" className={tabTrigger} data-testid="tab-steps">Projekttrin</TabsTrigger>
          <TabsTrigger value="economy" className={tabTrigger} data-testid="tab-economy"><Wallet className="h-4 w-4 mr-1.5" /> Økonomi</TabsTrigger>
          <TabsTrigger value="internal" className={tabTrigger} data-testid="tab-internal"><ClipboardList className="h-4 w-4 mr-1.5" /> Interne opgaver</TabsTrigger>
          <TabsTrigger value="comments" className={tabTrigger} data-testid="tab-comments"><MessageSquare className="h-4 w-4 mr-1.5" /> Kommunikation</TabsTrigger>
          <TabsTrigger value="minutes" className={tabTrigger} data-testid="tab-minutes"><NotebookText className="h-4 w-4 mr-1.5" /> Mødereferat</TabsTrigger>
          <TabsTrigger value="files" className={tabTrigger} data-testid="tab-files"><Paperclip className="h-4 w-4 mr-1.5" /> Filer</TabsTrigger>
        </TabsList>

        <TabsContent value="steps" className="mt-8">
          <div className="relative pl-8">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-[#EBE8E3]" />
            <div className="space-y-4">
              {steps.map((s, idx) => {
                const mine = (s.responsible_user_ids && s.responsible_user_ids.length ? s.responsible_user_ids : (s.responsible_user_id ? [s.responsible_user_id] : [])).includes(me.id);
                const editable = canEdit;
                const delayed = s.selected && s.status !== "Færdig" && s.expected_end_date && s.expected_end_date < today();
                const stStyle = STEP_STYLE[s.status] || STEP_STYLE["Ikke startet"];
                const dotColor = !s.selected ? "bg-white border-[#D6D3D1]" : delayed ? "bg-white border-[#A83232]" : stStyle.dot;
                const cardBorder = !s.selected ? "border-dashed border-[#EBE8E3] opacity-70" : delayed ? "border-[#A83232]/60" : stStyle.border;
                return (
                  <div key={s.id} className="relative" data-testid={`step-row-${idx}`}>
                    <span className={`absolute -left-8 top-6 h-3.5 w-3.5 rounded-full border-2 ${dotColor} z-10`} />
                    <div className={`bg-white border rounded-2xl p-5 transition-all ${cardBorder}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox checked={s.selected} disabled={!editable} onCheckedChange={(c) => patchStep(s.id, { selected: !!c })} className="mt-1 border-[#D6D3D1] data-[state=checked]:bg-stone-400 data-[state=checked]:border-stone-400 data-[state=checked]:text-white" data-testid={`step-selected-${idx}`} />
                        {editable && (
                          <div className="flex flex-col">
                            <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`step-up-${idx}`}><ChevronUp className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === steps.length - 1} onClick={() => move(idx, 1)} data-testid={`step-down-${idx}`}><ChevronDown className="h-4 w-4" /></Button>
                          </div>
                        )}
                        <div className="flex-1 min-w-0 space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            {editable ? (
                              <Input className="max-w-md h-8" defaultValue={s.name} onBlur={(e) => e.target.value !== s.name && patchStep(s.id, { name: e.target.value })} data-testid={`step-name-${idx}`} />
                            ) : <span className="font-semibold">{s.name}</span>}
                            <Badge variant="outline" className={`rounded-xl ${statusStyle[s.status]}`} data-testid={`step-status-badge-${idx}`}>{s.status}</Badge>
                            {delayed && <Badge className="rounded-xl bg-primary/10 text-primary border-primary/30">Forsinket</Badge>}
                          </div>
                          <div className="grid sm:grid-cols-5 gap-3 text-sm">
                            <div>
                              <p className="label-caps mb-1">Varighed</p>
                              {editable ? <div className="flex items-center gap-1"><Input type="number" min="0" className="h-8 w-20" defaultValue={s.duration_days} onBlur={(e) => parseInt(e.target.value) !== s.duration_days && patchStep(s.id, { duration_days: parseInt(e.target.value) || 0 })} data-testid={`step-duration-${idx}`} /><span className="text-muted-foreground">dage</span></div> : <p>{s.duration_days} dage</p>}
                            </div>
                            <div>
                              <p className="label-caps mb-1">Ansvarlige</p>
                              {editable ? (
                                <ResponsibleMultiSelect
                                  users={salgUsers}
                                  contactName={p.customer_contact?.name || null}
                                  value={[...(s.responsible_user_ids && s.responsible_user_ids.length ? s.responsible_user_ids : (s.responsible_user_id ? [s.responsible_user_id] : [])), ...(s.responsible_contact_name ? ["__contact__"] : [])]}
                                  onChange={(tokens) => patchStep(s.id, { responsible_user_ids: tokens.filter((t) => t !== "__contact__"), responsible_contact_name: tokens.includes("__contact__") ? (p.customer_contact?.name || null) : null })}
                                  testid={`step-resp-${idx}`}
                                  className="h-8"
                                />
                              ) : (() => {
                                const rs = (s.responsible_user_ids && s.responsible_user_ids.length ? s.responsible_user_ids : (s.responsible_user_id ? [s.responsible_user_id] : [])).map((id) => users.find((u) => u.id === id)).filter(Boolean);
                                const parts = rs.map((u) => <RespChip key={u.id} user={u} />);
                                if (s.responsible_contact_name) parts.push(<RespChip key="__contact__" user={{ id: "__contact__", name: `${s.responsible_contact_name} (kunde)` }} />);
                                if (!parts.length) return <p>{s.responsible_user_name || "—"}</p>;
                                return <div className="flex flex-wrap gap-1.5">{parts}</div>;
                              })()}
                            </div>
                            <div>
                              <p className="label-caps mb-1">Status</p>
                              <Select value={s.status} onValueChange={(v) => patchStep(s.id, { status: v })} disabled={!(editable || mine)}>
                                <SelectTrigger className="h-8" data-testid={`step-status-${idx}`}><SelectValue /></SelectTrigger>
                                <SelectContent>{STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div><p className="label-caps mb-1">Datoer</p><p className="text-muted-foreground">{daDate(s.start_date)} → {daDate(s.expected_end_date)}</p>{(() => { const hi = holidayInfo(s.start_date, s.expected_end_date); return hi ? (<p className="text-xs text-[#806B61] mt-1 flex items-start gap-1.5" data-testid={`step-holiday-${idx}`}><Info className="h-3 w-3 shrink-0 mt-0.5" strokeWidth={1.75} /> {holidayLabel(hi)}</p>) : null; })()}</div>
                            <div>
                              <p className="label-caps mb-1">Timer brugt</p>
                              {(editable || mine) ? (
                                <div className="flex items-center gap-1">
                                  <Input type="number" min="0" step="0.5" className="h-8 w-20" defaultValue={s.hours_spent ?? ""} placeholder="0" onBlur={(e) => { const v = e.target.value === "" ? null : parseFloat(e.target.value); if (v !== (s.hours_spent ?? null)) patchStep(s.id, { hours_spent: v }); }} data-testid={`step-hours-${idx}`} />
                                  <span className="text-muted-foreground">t</span>
                                </div>
                              ) : <p>{s.hours_spent != null ? `${s.hours_spent} t` : "—"}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="ghost" className="rounded-xl gap-1.5" onClick={() => setOpenStep(openStep === s.id ? null : s.id)} data-testid={`step-attach-btn-${idx}`}><Paperclip className="h-3.5 w-3.5" /> {stepFileCount(s.id) > 0 && <span className="text-xs font-semibold">{stepFileCount(s.id)}</span>}</Button>
                            {editable && <Button size="sm" variant="ghost" className="rounded-xl text-destructive hover:text-destructive ml-auto" onClick={() => delStep(s.id)} data-testid={`step-delete-${idx}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Fjern</Button>}
                          </div>
                          {openStep === s.id && <FileArea projectId={id} stepId={s.id} files={files} onChange={loadFiles} canEdit={canEdit} />}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {canEdit && <Button variant="outline" className="rounded-full mt-6 ml-8 border-[#EBE8E3]" onClick={addStep} data-testid="add-project-step"><Plus className="h-4 w-4 mr-1" /> Tilføj projekttrin</Button>}
        </TabsContent>

        <TabsContent value="files" className="mt-8">
          <div className="pcard p-6 max-w-2xl">
            <h3 className="font-semibold tracking-tight mb-1 text-[#1A1919] flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" /> Projektfiler</h3>
            <p className="text-sm text-muted-foreground mb-4">Vedhæft tegninger og dokumenter til hele projektet.</p>
            <FileArea projectId={id} stepId={null} files={files} onChange={loadFiles} canEdit={canEdit} />
          </div>
        </TabsContent>

        <TabsContent value="economy" className="mt-8">
          <EconomyPanel steps={steps} canEdit={canEdit} onSaveBudget={saveBudget} lines={p.economy_lines || []} onSaveLines={saveEconomyLines} />
        </TabsContent>

        <TabsContent value="internal" className="mt-8">
          <ProjectCases projectId={id} customerId={p.customer_id} customerName={p.customer_name} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="comments" className="mt-8">
          <CommentsCard projectId={id} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="minutes" className="mt-8">
          <MinutesCard projectId={id} canEdit={canEdit} />
        </TabsContent>
      </Tabs>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Del projekt med kunde</DialogTitle>
            <DialogDescription>Et skrivebeskyttet link til projektets tidslinje og status. Kunden kan følge med uden login — siden opdateres automatisk.</DialogDescription>
          </DialogHeader>
          {p.share_token ? (
            <div className="space-y-5">
              <div className="flex gap-2">
                <Input readOnly value={shareUrl} className="text-sm" data-testid="share-link-input" onFocus={(e) => e.target.select()} />
                <Button className="rounded-full shrink-0 bg-[#A83232] hover:bg-[#8f2a2a]" onClick={copyShare} data-testid="copy-share-link"><Copy className="h-4 w-4" /></Button>
              </div>
              <ShareExpiry kind="project" docId={id} expiresAt={p.share_expires_at} onChange={(v) => setP({ ...p, share_expires_at: v })} />
              <div className="flex flex-wrap gap-2">
                <a href={shareUrl} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="rounded-full" data-testid="open-share-link"><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Se som kunde</Button></a>
                <Button variant="ghost" size="sm" className="rounded-full" onClick={regenShare} data-testid="regen-share-link">Nulstil link</Button>
                <Button variant="ghost" size="sm" className="rounded-full text-destructive hover:text-destructive ml-auto" onClick={disableShare} data-testid="disable-share-link"><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Deaktivér</Button>
              </div>
              <div className="border-t border-border pt-4">
                <p className="label-caps mb-3">Hvad må kunden se?</p>
                <div className="space-y-2.5">
                  {[["description", "Projektbeskrivelse"], ["progress", "Fremdrift"], ["phases", "Projektfaser"], ["responsibles", "Ansvarlige personer"], ["deadlines", "Deadlines"], ["documents", "Dokumenter"], ["contact", "Kontaktoplysninger"], ["economy", "Økonomi"], ["minutes", "Mødereferater"]].map(([key, label]) => (
                    <label key={key} className="flex items-center justify-between text-sm cursor-pointer">
                      <span>{label}</span>
                      <Switch checked={ss[key] !== false} onCheckedChange={(v) => setShare(key, v)} data-testid={`share-setting-${key}`} />
                    </label>
                  ))}                </div>
                <p className="text-xs text-muted-foreground mt-3">Kun det, der er slået til, vises på kundelinket.</p>
              </div>
            </div>
          ) : (
            <Button className="rounded-full font-semibold bg-[#A83232] hover:bg-[#8f2a2a]" onClick={createShare} data-testid="create-share-link"><Share2 className="h-4 w-4 mr-2" /> Opret delbart link</Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
